import Bugsnag from '@bugsnag/js'
import { Alert, Box, Button, CircularProgress, Grid, Slider, Typography } from '@mui/material'
import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react'
import { MdNavigateNext } from 'react-icons/md'
import { useLocation, useNavigate, useNavigationType } from 'react-router-dom'
import { Formtitle, Layout, PositioningResult } from '../components'
import { client } from '../utils/client'
import { FormType, PositioningOutcomeType } from '../utils/sharedTypes'
type PositionnementPropsType = {
  form: FormType
  setForm: Dispatch<SetStateAction<FormType>>
}

type PositioningItem = {
  global_id: string
  label: string
  text: string | null
  positioning_question: string | null
  position: number
  score: number | null
}

type QuestionnairePayload = {
  product_code: string
  skills_title: string | null
  skills_introduction: string | null
  items: PositioningItem[]
  status: string
}

const SCORES = [1, 2, 3, 4, 5] as const
const DEFAULT_SCORE = 2

const SCORE_LABELS: Record<number, string> = {
  1: '1 — Je ne connais pas ce sujet',
  2: "2 — J'ai quelques notions de ce sujet",
  3: '3 — Je connais ce sujet assez bien',
  4: '4 — Je maîtrise ce sujet',
  5: '5 — Je maîtrise ce sujet parfaitement',
}

const getScoreValueLabel = (value: number) => {
  return SCORE_LABELS[value] ?? String(value)
}

const Positionnement = ({ form, setForm }: PositionnementPropsType) => {
  const navigate = useNavigate()
  const location = useLocation()
  const navigationType = useNavigationType()
  const [questionnaire, setQuestionnaire] = useState<QuestionnairePayload | null>(null)
  const [scores, setScores] = useState<(number | null)[]>([])
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [completedOutcome, setCompletedOutcome] = useState<PositioningOutcomeType | null>(null)
  const [completeError, setCompleteError] = useState<string | null>(null)
  const [questionnaireError, setQuestionnaireError] = useState<string | null>(null)

  const pendingProduct = useMemo(() => {
    const products = form.data.positioning_status?.products || []
    return products.find((p) => p.required && !p.completed)
  }, [form.data.positioning_status])

  const completedProduct = useMemo(() => {
    const products = form.data.positioning_status?.products || []
    return products.find((p) => p.completed) ?? null
  }, [form.data.positioning_status])

  const fetchPositioningOutcome = useCallback(
    async (productCode: string) => {
      const resp = await client.get(
        `/registration/${form.data.global_id}/positioning/outcome/?product_code=${productCode}`,
      )
      return (resp.data?.outcome as PositioningOutcomeType | undefined) ?? null
    },
    [form.data.global_id],
  )

  const navigateNext = useCallback(() => {
    // Suivre l'ordre réel du parcours : aller à l'étape qui suit le positionnement.
    // Cela évite de sauter le financement (ex. FIFPL/FAFPM : information → positionnement → financement → récapitulatif).
    const positionnementIndex = form.steps.findIndex((s) => s.url === 'positionnement')
    const nextStep = positionnementIndex !== -1 ? form.steps[positionnementIndex + 1] : undefined
    if (nextStep) {
      navigate({ pathname: `../${nextStep.url}`, search: location.search })
      return
    }
    // Repli : le positionnement ne fait pas partie des étapes (positionnement non applicable)
    const code = form.data.funding_organism?.code
    if (code === 'CPF') {
      navigate({ pathname: '../cpf', search: location.search })
    } else if (code === 'DPC') {
      navigate({ pathname: '../pre-inscription', search: location.search })
    } else if (code === 'FIFPL') {
      navigate({ pathname: '../recapitulatif', search: location.search })
    } else {
      // FAFPM et autres : financement avant le récapitulatif
      navigate({ pathname: '../financement', search: location.search })
    }
  }, [form.steps, form.data.funding_organism?.code, location.search, navigate])

  const refreshRegistration = useCallback(async () => {
    const resp = await client.get(`/registration/${form.data.global_id}/`)
    const data = resp.data as FormType['data']
    setForm((prev) => ({ ...prev, data: { ...prev.data, ...data } }))
    return data
  }, [form.data.global_id, setForm])

  useEffect(() => {
    // Ne pas rediriger lors d'un retour navigateur (POP) : laisser l'historique fonctionner normalement
    if (navigationType === 'POP') return
    if (form.data.positioning_status === undefined) return

    const products = form.data.positioning_status.products ?? []
    const hasPending = products.some((p) => p.required && !p.completed)
    const hasCompleted = products.some((p) => p.completed)

    // Rediriger uniquement si le positionnement ne s'applique pas (aucun produit actif ni complété)
    if (products.length === 0 || (!hasPending && !hasCompleted && !form.data.positioning_status.required)) {
      if (!completedOutcome && !completeError) {
        navigateNext()
      }
    }
  }, [
    form.data.positioning_status,
    navigateNext,
    completedOutcome,
    completeError,
    navigationType,
  ])

  useEffect(() => {
    if (completedOutcome || completeError || pendingProduct || !completedProduct?.product_code || !form.data.global_id) {
      return
    }
    setLoading(true)
    fetchPositioningOutcome(completedProduct.product_code)
      .then((outcome) => {
        if (outcome) {
          setCompletedOutcome(outcome)
        } else {
          setCompleteError(
            'Votre positionnement a bien été enregistré, mais le détail du parcours personnalisé est momentanément indisponible.',
          )
        }
      })
      .catch((err: Error) => {
        Bugsnag.notify(err)
        console.error(err)
        setCompleteError('Impossible de charger le résultat de votre positionnement. Veuillez réessayer.')
      })
      .finally(() => setLoading(false))
  }, [
    completedProduct?.product_code,
    pendingProduct,
    form.data.global_id,
    completedOutcome,
    completeError,
    fetchPositioningOutcome,
  ])

  const loadQuestionnaire = useCallback(() => {
    if (completedOutcome || !pendingProduct?.product_code || !form.data.global_id) return
    setQuestionnaireError(null)
    setLoading(true)
    client
      .get(`/registration/${form.data.global_id}/positioning/?product_code=${pendingProduct.product_code}`)
      .then((resp) => {
        const payload = resp.data as QuestionnairePayload
        setQuestionnaire(payload)
        setScores(payload.items.map((item) => item.score ?? DEFAULT_SCORE))
      })
      .catch((err: Error) => {
        Bugsnag.notify(err)
        console.error(err)
        setQuestionnaireError('Impossible de charger le questionnaire. Veuillez réessayer.')
      })
      .finally(() => setLoading(false))
  }, [completedOutcome, pendingProduct?.product_code, form.data.global_id])

  useEffect(() => {
    loadQuestionnaire()
  }, [loadQuestionnaire])

  const handleScoreChange = (index: number, score: number) => {
    const itemCount = questionnaire?.items.length ?? 0
    setScores((prev) => {
      const next = Array.from({ length: itemCount }, (_, i) => prev[i] ?? null)
      next[index] = score
      return next
    })
  }

  const persistScore = (index: number, score: number) => {
    const item = questionnaire?.items[index]
    if (!item?.global_id) return
    client
      .patch(`/registration/${form.data.global_id}/positioning/?product_code=${pendingProduct?.product_code}`, {
        responses: [{ skill_global_id: item.global_id, score }],
      })
      .catch((err: Error) => {
        Bugsnag.notify(err)
        console.error(err)
      })
  }

  const getEffectiveScore = (index: number) => scores[index] ?? DEFAULT_SCORE

  const canSubmit =
    (questionnaire?.items.length ?? 0) > 0 &&
    (questionnaire?.items ?? []).every((_, index) => {
      const score = getEffectiveScore(index)
      return score >= SCORES[0] && score <= SCORES[SCORES.length - 1]
    })

  const handleComplete = async () => {
    if (!pendingProduct?.product_code || !canSubmit) return
    setSubmitting(true)
    setCompleteError(null)
    const productCode = pendingProduct.product_code
    try {
      const items = questionnaire?.items ?? []
      const responses = items
        .map((item, idx) => ({ skill_global_id: item.global_id, score: getEffectiveScore(idx) }))
        .filter((r) => r.skill_global_id)
      await client.patch(`/registration/${form.data.global_id}/positioning/?product_code=${productCode}`, { responses })
      const resp = await client.post(
        `/registration/${form.data.global_id}/positioning/complete/?product_code=${productCode}`,
      )
      let outcome = resp.data?.outcome as PositioningOutcomeType | undefined
      if (!outcome) {
        outcome = (await fetchPositioningOutcome(productCode)) ?? undefined
      }
      if (outcome) {
        setCompletedOutcome(outcome)
      } else {
        setCompleteError(
          'Votre positionnement a bien été enregistré, mais le détail du parcours personnalisé est momentanément indisponible.',
        )
      }
      await refreshRegistration()
    } catch (err) {
      Bugsnag.notify(err as Error)
      console.error(err)
      setCompleteError('Une erreur est survenue lors de la validation du test. Veuillez réessayer.')
    } finally {
      setSubmitting(false)
    }
  }

  useEffect(() => {
    if (!completedOutcome?.product_code || completedOutcome.positioning_document_url) return
    let attempts = 0
    const intervalId = window.setInterval(() => {
      attempts += 1
      if (attempts > 12) {
        window.clearInterval(intervalId)
        return
      }
      fetchPositioningOutcome(completedOutcome.product_code).then((outcome) => {
        if (outcome?.positioning_document_url) {
          setCompletedOutcome(outcome)
          window.clearInterval(intervalId)
        }
      })
    }, 3000)
    return () => window.clearInterval(intervalId)
  }, [completedOutcome?.product_code, completedOutcome?.positioning_document_url, fetchPositioningOutcome])

  const handleContinueFromResults = async () => {
    setCompletedOutcome(null)
    setCompleteError(null)
    setQuestionnaireError(null)
    setQuestionnaire(null)
    setScores([])
    const data = await refreshRegistration()
    const stillPending = data.positioning_status?.products?.some((p) => p.required && !p.completed)
    if (!stillPending) {
      navigateNext()
    }
  }

  if (!pendingProduct && !completedOutcome && !completeError) {
    return (
      <Layout form={form.data} steps={form.steps}>
        <Grid item xs={12}>
          <CircularProgress />
        </Grid>
      </Layout>
    )
  }

  return (
    <Layout form={form.data} steps={form.steps}>
      <Grid item container spacing={1} sx={{ justifyContent: 'flex-start' }}>
        <Formtitle
          title={completedOutcome || completeError ? 'Votre parcours personnalisé' : 'Test de positionnement'}
        />
        <Grid item xs={12}>
          {!completedOutcome && !completeError && (
            <Alert severity="info" sx={{ backgroundColor: '#e3f0ff', color: '#000' }}>
              Ce test de positionnement nous permet d’adapter la durée du parcours et de la session à votre niveau, en
              fonction de votre positionnement sur chacun des objectifs de la formation.
            </Alert>
          )}
        </Grid>
        {completedOutcome || completeError ? (
          <>
            {completedOutcome && <PositioningResult outcome={completedOutcome} />}
            {completeError && (
              <Grid item xs={12}>
                <Alert severity={completedOutcome ? 'warning' : 'error'}>{completeError}</Alert>
              </Grid>
            )}
            <Grid item xs={12} sx={{ mt: 2 }}>
              <Button variant="contained" endIcon={<MdNavigateNext />} onClick={handleContinueFromResults}>
                Continuer
              </Button>
            </Grid>
          </>
        ) : (
          <>
            {loading && (
              <Grid item xs={12}>
                <CircularProgress size={32} />
              </Grid>
            )}
            {!loading && questionnaireError && (
              <Grid item xs={12}>
                <Alert severity="error" sx={{ mb: 2 }}>
                  {questionnaireError}
                </Alert>
                <Button variant="outlined" onClick={loadQuestionnaire}>
                  Réessayer
                </Button>
              </Grid>
            )}
            {!loading && questionnaire && (
              <>
                {questionnaire.items.map((item, index) => (
                  <Grid item xs={12} key={`${item.global_id ?? 'skill'}-${item.position}-${index}`}>
                    <Box sx={{ py: 2, borderBottom: '1px solid #eee' }}>
                      {item.positioning_question && (
                        <Typography variant="body1" color="text.primary" fontWeight="bold" sx={{ mb: 1 }}>
                          {item.positioning_question}
                        </Typography>
                      )}
                      {!item.positioning_question && (
                        <>
                          <Typography variant="body1" color="text.primary" fontWeight="bold">
                            {item.label}
                          </Typography>
                          {item.text && (
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                              {item.text}
                            </Typography>
                          )}
                        </>
                      )}
                      <Slider
                        aria-label={`Niveau de maîtrise pour ${item.label}`}
                        value={scores[index] ?? DEFAULT_SCORE}
                        valueLabelDisplay="auto"
                        valueLabelFormat={getScoreValueLabel}
                        marks
                        onChange={(_e, value) => handleScoreChange(index, value as number)}
                        onChangeCommitted={(_e, value) => persistScore(index, value as number)}
                        step={1}
                        min={SCORES[0]}
                        max={SCORES[SCORES.length - 1]}
                        defaultValue={DEFAULT_SCORE}
                        sx={{
                          mt: 0,
                          px: 1,
                          '& .MuiSlider-mark': {
                            width: 6,
                            height: 6,
                            borderRadius: '50%',
                          },
                        }}
                      />
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" sx={{ cursor: 'pointer', fontSize: '0.7rem' }}>
                          {SCORES[0]} = je ne connais rien à ce sujet
                        </Typography>
                        <Typography variant="body2" sx={{ cursor: 'pointer', fontSize: '0.7rem', textAlign: 'right' }}>
                          {SCORES[SCORES.length - 1]} = je maîtrise ce sujet parfaitement
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                ))}
                <Grid item xs={12} sx={{ mt: 2 }}>
                  <Button
                    variant="contained"
                    endIcon={<MdNavigateNext />}
                    disabled={!canSubmit || submitting}
                    onClick={handleComplete}
                  >
                    {submitting ? 'Calcul du parcours…' : 'Valider mon positionnement'}
                  </Button>
                </Grid>
              </>
            )}
          </>
        )}
      </Grid>
    </Layout>
  )
}

export default Positionnement
